import PLILogoUrl from '../assets/pli-logo.svg';
import {
  FooterBlock as FooterBlockProps,
  HeaderBlock as HeaderBlockProps,
  ReferencePageSettings
} from '@/@types/content';
import EditorialToutWithImageBlock from '@/components/blocks/EditorialToutWithImageBlock';
import { LinkItem } from '@/@types/client-api';
import { useNavigate } from 'react-router-dom';
import withNav from '@/components/hocs/withNav';
import PageErrorImage from '../assets/images/page_error_image.png';
import { useLazyGetRedirectForUrlQuery } from '@/redux/api/client/redirect';
import { useEffect, useRef } from 'react';
import { trackPageExceptionEvent } from '@/analytics/pages';
import { datadogRum } from '@/lib/DatadogService';
import Shimmer from '@/components/ui/Shimmer';

interface ErrorPageProps {
  title?: string;
  message: string;
  copy?: string;
  headerBlock?: HeaderBlockProps;
  footerBlock?: FooterBlockProps;
  siteLabels?: ReferencePageSettings;
  possibleRedirectTo?: string;
}

const ErrorPageComponent: React.FC<ErrorPageProps> = ({
  title = 'We’re sorry, our website is undergoing maintenance and is currently unavailable.',
  copy = `In order to access it, please reload the page. If the issue persists please contact customer service at ${import.meta.env.VITE_APP_SERVICE_PHONE_NUMBER} or ${import.meta.env.VITE_APP_INFO_EMAIL}.`,
  message,
  possibleRedirectTo
}) => {
  const navigate = useNavigate();
  const [getReidrectForUrlQuery] = useLazyGetRedirectForUrlQuery();
  const firstLoad = useRef(true);
  const isWaiting = useRef(true);
  const isRequesting = useRef(false);

  useEffect(() => {
    if (firstLoad.current) {
      trackPageExceptionEvent();
      firstLoad.current = false;
    }
    if (possibleRedirectTo) {
      if (!isRequesting.current) {
        isRequesting.current = true;
        getReidrectForUrlQuery(possibleRedirectTo)
          .unwrap()
          .then(response => {
            if (response) {
              document.location.href = response;
            } else {
              isWaiting.current = false;
            }
          })
          .catch(e => {
            datadogRum.addError(e);
            isWaiting.current = false;
          });
      }
    } else {
      isWaiting.current = false;
    }
  });

  return isWaiting.current ? (
    <Shimmer className="h-64 w-full" />
  ) : (
    <div className="flex h-[1319px] min-h-screen flex-col" data-component={'ErrorPage'}>
      <main className="container grow py-[100px]">
        <EditorialToutWithImageBlock
          smallImage={false}
          onPrimaryButtonClick={() => {
            navigate(0);
          }}
          heading={title}
          copy={copy}
          link1RenderAsButton={true}
          aspectRadioLayout="1:1"
          link1Style="primary"
          eyebrowText={message}
          eyebrowSize="small"
          requireAuthOnLinks={true}
          logo={{
            url: PLILogoUrl
          }}
          image={{
            url: PageErrorImage
          }}
          layout="media-right"
          imagePlacement="above-text"
          primaryLink={
            {
              title: 'Try Again',
              url: '',
              text: 'Try Again'
            } as LinkItem
          }
        />
      </main>
    </div>
  );
};

const ErrorPage = withNav(ErrorPageComponent);
export default ErrorPage;
